import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  ChevronDown,
  Clapperboard,
  Layers,
  MessageCircle,
  ShoppingBag,
  Sparkles,
  Star,
  Users,
  WandSparkles,
} from 'lucide-react';
import { postService, type WorkSummary } from '../../services/postService';
import WorkCard from '../../components/UI/WorkCard';
import WorkDetailModal, { type WorkDetail } from '../../components/Profile/work/WorkDetailModal';
import {
  STUDIO_CASE_CATEGORIES,
  matchesStudioCaseCategory,
  type StudioCaseCategoryId,
} from '../../constants/studio';
import {
  DEFAULT_OFFICIAL_USER,
  createStudioWorkDetail,
  loadOfficialStudioPortfolio,
  mergeStudioWorkDetail,
  type StudioOfficialUser,
} from './studioPortfolio';
import { OFFICIAL_STUDIO_INDUSTRIES_PATH } from '../../constants/official';
import StudioContactSection from './StudioContactSection';
import './Studio.css';

const stats = [
  { icon: Star, value: '50+', label: '完成项目' },
  { icon: Users, value: '30+', label: '合作品牌' },
  { icon: Layers, value: '98%', label: '客户满意度' },
  { icon: Sparkles, value: '∞', label: '创意可能' },
];

const services = [
  {
    icon: Clapperboard,
    title: 'TVC / 广告创作',
    text: '从传播策略、创意概念、脚本分镜到制作成片，把一句 Brief 发展成有情绪、有节奏，也有传播力的品牌影片。',
  },
  {
    icon: Building2,
    title: '品牌 / 企业宣传片',
    text: '覆盖品牌故事、企业形象与产品发布，让抽象的品牌价值被看见，让专业实力留下清晰而持久的印象。',
  },
  {
    icon: ShoppingBag,
    title: '电商 / 营销内容',
    text: '围绕主图、KV、详情页、创意短片与投放素材，高效完成多平台、多尺寸、多版本交付，兼顾视觉质感与营销转化。',
  },
  {
    icon: WandSparkles,
    title: 'AI / 创意影像',
    text: '融合动画、摄影、平面设计与 AIGC 工作流，突破传统制作的场景与成本边界，把难以拍摄的想象带进现实。',
  },
];

interface StudioTeamMember {
  id: string;
  englishName: string;
  chineseName: string;
  focus: string;
  avatar: string;
  credentials: string[];
  summary: string;
}

const teamMembers: StudioTeamMember[] = [
  {
    id: 'chao',
    englishName: 'Chao',
    chineseName: '张朝',
    focus: '动画导演 · AI 视频',
    avatar: '/assets/team/chao.png',
    credentials: [
      '可灵 AI 超级创作者',
      'Tapnow 签约导演',
    ],
    summary: '8 年高级动画设计经验，全国设计大赛冠军、谷歌官方动画创作者、可灵 AI 视频大赛二等奖。',
  },
  {
    id: 'max',
    englishName: 'Max',
    chineseName: '林鑫亮',
    focus: '动画设计 · AIGC 工作流',
    avatar: '/assets/team/max.png',
    credentials: [
      '海螺 AI 超级创作者',
      '即梦优质创作者',
    ],
    summary: '8 年高级动画设计经验，擅长融合传统动画流程与 AIGC 技术，熟悉工作流搭建及智能体 Skill 开发。',
  },
  {
    id: 'tutu',
    englishName: '图图',
    chineseName: '黄雅图',
    focus: '品牌设计 · 虚拟 IP',
    avatar: '/assets/team/tutu.jpg',
    credentials: [
      '东京女子美术大学平面设计',
      '新锐 AIGC 艺术家',
    ],
    summary: '长期从事品牌设计与视觉内容制作，专注 AI 视觉在品牌传播、虚拟 IP 与新媒体影像中的商业落地。',
  },
  {
    id: 'chloe',
    englishName: 'Chloe',
    chineseName: '应晓云',
    focus: '摄影 · AI 影像',
    avatar: '/assets/team/chloe.jpg',
    credentials: [
      '伦敦艺术大学摄影硕士',
      '马兰戈尼学院摄影导师',
    ],
    summary: '拥有 8 年以上商业时尚摄影与品牌视觉经验，擅长完整影像流程，以及 AIGC 视觉与实拍结合。',
  },
  {
    id: 'roc',
    englishName: 'Roc',
    chineseName: '万礼鹏',
    focus: '项目统筹 · 商务制片',
    avatar: '/assets/team/roc.png',
    credentials: [
      '新加坡国立大学计算机科学硕士',
      'AIGC 制片人',
    ],
    summary: '6 年软件开发经验，曾跟随头部 AI 导演创作，后担任 AIGC 制片人并创立小茉莉工作室。兼具理性统筹与艺术判断，擅长梳理需求、把控细节和推进交付。',
  },
];

const teamGroups = [
  {
    id: 'artists',
    title: 'AI 艺术家',
    members: teamMembers.slice(0, 4),
  },
  {
    id: 'production',
    title: '商务 / 制片',
    members: teamMembers.slice(4),
  },
];

interface StudioBrandLogo {
  id: string;
  name: string;
  logo: string;
}

const brandLogos: StudioBrandLogo[] = [
  { id: 'microsoft', name: 'Microsoft', logo: '/assets/brands/microsoft.svg' },
  { id: 'hermes', name: 'Hermès', logo: '/assets/brands/hermes.svg' },
  { id: 'xiaohongshu', name: '小红书', logo: '/assets/brands/xiaohongshu.svg' },
  { id: 'china-telecom', name: '中国电信', logo: '/assets/brands/china-telecom.svg' },
  { id: 'miumiu', name: 'MIU MIU', logo: '/assets/brands/miumiu.svg' },
  { id: 'douyin', name: '抖音', logo: '/assets/brands/douyin.svg' },
  { id: 'tmall', name: '天猫', logo: '/assets/brands/tmall.svg' },
  { id: 'burberry', name: 'Burberry', logo: '/assets/brands/burberry.svg' },
  { id: 'meituan', name: '美团', logo: '/assets/brands/meituan.svg' },
  { id: 'ysl', name: 'YSL', logo: '/assets/brands/ysl.svg' },
  { id: 'swarovski', name: '施华洛世奇', logo: '/assets/brands/swarovski.svg' },
  { id: 'tcl', name: 'TCL', logo: '/assets/brands/tcl.svg' },
  { id: 'cscec', name: '中国建筑', logo: '/assets/brands/cscec.svg' },
  { id: 'vcg', name: '视觉中国', logo: '/assets/brands/vcg.svg' },
  { id: 'w-management', name: 'W.Management', logo: '/assets/brands/w-management.svg' },
];

const INITIAL_CASE_COUNT = 8;

const revealDelay = (delay: number) => ({
  '--studio-reveal-delay': `${delay}ms`,
} as React.CSSProperties);

const Studio = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const caseExpandRef = useRef<HTMLDivElement>(null);
  const caseExpandStartTopRef = useRef<number | null>(null);
  const caseExpandAnimationRef = useRef<Animation | null>(null);
  const [officialUser, setOfficialUser] = useState<StudioOfficialUser>(DEFAULT_OFFICIAL_USER);
  const [officialWorks, setOfficialWorks] = useState<WorkSummary[]>([]);
  const [worksLoading, setWorksLoading] = useState(true);
  const [casesExpanded, setCasesExpanded] = useState(false);
  const [activeCaseCategory, setActiveCaseCategory] = useState<StudioCaseCategoryId>('all');
  const [selectedWork, setSelectedWork] = useState<WorkDetail | null>(null);

  const filteredWorks = useMemo(
    () => officialWorks.filter((work) => matchesStudioCaseCategory(work, activeCaseCategory)),
    [activeCaseCategory, officialWorks]
  );
  const initialWorks = filteredWorks.slice(0, INITIAL_CASE_COUNT);
  const additionalWorks = filteredWorks.slice(INITIAL_CASE_COUNT);
  const canExpandCases = additionalWorks.length > 0;

  const handleCasesToggle = useCallback(() => {
    caseExpandAnimationRef.current?.cancel();
    caseExpandStartTopRef.current = caseExpandRef.current?.getBoundingClientRect().top ?? null;
    setCasesExpanded((current) => !current);
  }, []);

  const createDetailFromSummary = useCallback(
    (work: WorkSummary) => createStudioWorkDetail(work, officialUser),
    [officialUser]
  );

  const handleWorkClick = useCallback(async (work: WorkSummary) => {
    setSelectedWork(createDetailFromSummary(work));

    const detail = await postService.getPostDetail(String(work.id));
    if (!detail) return;

    setSelectedWork((current) => {
      if (!current || current.id !== work.id) {
        return current;
      }

      return mergeStudioWorkDetail(current, detail);
    });
  }, [createDetailFromSummary]);

  useEffect(() => {
    let isActive = true;

    const loadOfficialWorks = async () => {
      setWorksLoading(true);
      try {
        const portfolio = await loadOfficialStudioPortfolio();
        if (!isActive) return;
        setOfficialUser(portfolio.officialUser);
        setOfficialWorks(portfolio.works);
      } catch (error) {
        console.error('Failed to load official studio works:', error);
        if (isActive) {
          setOfficialWorks([]);
        }
      } finally {
        if (isActive) {
          setWorksLoading(false);
        }
      }
    };

    void loadOfficialWorks();
    window.addEventListener('posts_updated', loadOfficialWorks);

    return () => {
      isActive = false;
      window.removeEventListener('posts_updated', loadOfficialWorks);
    };
  }, []);

  useLayoutEffect(() => {
    const expandRow = caseExpandRef.current;
    const startTop = caseExpandStartTopRef.current;
    caseExpandStartTopRef.current = null;

    if (!expandRow || startTop === null) return;

    const deltaY = startTop - expandRow.getBoundingClientRect().top;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || Math.abs(deltaY) < 1 || typeof expandRow.animate !== 'function') return;

    const animation = expandRow.animate([
      { transform: `translate3d(0, ${deltaY}px, 0)` },
      { transform: 'translate3d(0, 0, 0)' },
    ], {
      duration: 560,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    });

    caseExpandAnimationRef.current = animation;
    animation.onfinish = () => {
      if (caseExpandAnimationRef.current === animation) {
        caseExpandAnimationRef.current = null;
      }
    };
  }, [casesExpanded]);

  useEffect(() => () => {
    caseExpandAnimationRef.current?.cancel();
  }, []);

  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return;

    const revealElements = Array.from(
      page.querySelectorAll<HTMLElement>('[data-studio-reveal]')
    );
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || typeof IntersectionObserver === 'undefined') {
      revealElements.forEach((element) => element.classList.add('is-revealed'));
      return;
    }

    page.classList.add('is-motion-ready');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px',
    });

    revealElements.forEach((element) => {
      if (!element.classList.contains('is-revealed')) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
      page.classList.remove('is-motion-ready');
    };
  }, [activeCaseCategory, casesExpanded, filteredWorks.length, worksLoading]);

  return (
    <div className="studio-page" ref={pageRef}>
      <section className="studio-hero" aria-labelledby="studio-hero-title">
        <img
          className="studio-hero__background"
          src="/assets/about-hero-cloud-bg-16x9.png"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
        />
        <div className="studio-hero__content">
          <div
            className="studio-hero__corner"
            data-studio-reveal="clip"
            aria-hidden="true"
          />
          <div className="studio-title-row">
            <h1 id="studio-hero-title">
              <span
                className="studio-title-brand"
                data-studio-reveal="up"
                style={revealDelay(40)}
              >
                CraftCloud
              </span>
              <span className="studio-title-lockup">
                <strong data-studio-reveal="up" style={revealDelay(130)}>云作</strong>
                <em data-studio-reveal="up" style={revealDelay(220)}>Studio</em>
              </span>
            </h1>
          </div>
          <p className="studio-hero__copy" data-studio-reveal="up" style={revealDelay(290)}>
            专注 AIGC 视觉内容创作与品牌视觉策略，帮助创意落地，连接未来与想象。
          </p>
          <div className="studio-hero__actions" data-studio-reveal="up" style={revealDelay(370)}>
            <a className="studio-button studio-button--primary" href="#studio-cases">
              查看作品案例
              <ArrowRight aria-hidden="true" />
            </a>
            <a className="studio-button studio-button--secondary" href="#studio-services">
              了解我们的服务
            </a>
          </div>
        </div>
      </section>

      <main className="studio-main">
        <section className="studio-stats" aria-label="云作 studio 数据">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                className="studio-stat"
                key={stat.label}
                data-studio-reveal="scale"
                style={revealDelay(index * 65)}
              >
                <span className="studio-stat__icon" aria-hidden="true">
                  <Icon />
                </span>
                <div>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              </div>
            );
          })}
        </section>

        <section className="studio-cases" id="studio-cases" aria-labelledby="studio-cases-title">
          <div className="studio-section-head" data-studio-reveal="up">
            <h2 id="studio-cases-title">精选案例</h2>
            <div className="studio-case-tabs" role="group" aria-label="案例分类">
              {STUDIO_CASE_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  aria-pressed={activeCaseCategory === category.id}
                  className={activeCaseCategory === category.id ? 'is-active' : ''}
                  onClick={() => {
                    setActiveCaseCategory(category.id);
                    setCasesExpanded(false);
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
            <Link className="studio-section-link" to={OFFICIAL_STUDIO_INDUSTRIES_PATH}>
              按行业查看案例
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>

          {worksLoading ? (
            <div className="studio-case-state" role="status" data-studio-reveal="fade">正在加载官方作品…</div>
          ) : officialWorks.length === 0 ? (
            <div className="studio-case-state" data-studio-reveal="fade">暂无官方作品</div>
          ) : filteredWorks.length === 0 ? (
            <div className="studio-case-state" data-studio-reveal="fade">该分类暂时还没有案例</div>
          ) : (
            <>
              <div
                className="studio-case-grid"
                key={activeCaseCategory}
              >
                {initialWorks.map((work, index) => (
                  <div
                    key={work.id}
                    className="studio-case-work"
                    role="button"
                    tabIndex={0}
                    data-studio-reveal="scale"
                    style={revealDelay(Math.min(index * 45, 315))}
                    aria-label={`查看作品：${work.title}`}
                    onClick={() => void handleWorkClick(work)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        void handleWorkClick(work);
                      }
                    }}
                  >
                    <WorkCard
                      work={work}
                      variant="profile"
                      showEngagementStats={false}
                    />
                  </div>
                ))}
              </div>

              {casesExpanded && additionalWorks.length > 0 && (
                <div className="studio-case-grid studio-case-grid--more" aria-label="更多作品">
                  {additionalWorks.map((work, index) => (
                    <div
                      key={work.id}
                      className="studio-case-work"
                      role="button"
                      tabIndex={0}
                      data-studio-reveal="scale"
                      style={revealDelay(Math.min(index * 45, 315))}
                      aria-label={`查看作品：${work.title}`}
                      onClick={() => void handleWorkClick(work)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          void handleWorkClick(work);
                        }
                      }}
                    >
                      <WorkCard
                        work={work}
                        variant="profile"
                        showEngagementStats={false}
                      />
                    </div>
                  ))}
                </div>
              )}

              {canExpandCases && (
                <div className="studio-case-expand" data-studio-reveal="fade" ref={caseExpandRef}>
                  <button
                    type="button"
                    className={`studio-case-expand__button${casesExpanded ? ' is-expanded' : ''}`}
                    aria-label={casesExpanded ? '收起作品' : '展开全部作品'}
                    aria-expanded={casesExpanded}
                    onClick={handleCasesToggle}
                  >
                    <ChevronDown aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <section className="studio-services" id="studio-services" aria-labelledby="studio-services-title">
          <div className="studio-services__intro" data-studio-reveal="left">
            <p className="studio-kicker">服务介绍</p>
            <h2 id="studio-services-title">
              让审美与算法并肩，
              <span>把想象交付成片。</span>
            </h2>
            <div className="studio-services__lead">
              <p>我们把服务国际品牌与头部平台的经验，带进一间更轻、更直接的创作工作室。</p>
              <p>
                动画、平面、时尚摄影与 AI 技术协同创作，核心团队从创意到成片全程参与。减少层层转包，让预算更多留在创意与画面里，以更好的质价比，交付真正能代表品牌的内容。
              </p>
            </div>
            <a className="studio-button studio-button--primary" href="#studio-contact">
              <MessageCircle aria-hidden="true" />
              聊一聊
            </a>
          </div>
          <ol className="studio-service-list">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <li
                  className="studio-service"
                  key={service.title}
                  data-studio-reveal="right"
                  style={revealDelay(index * 70)}
                >
                  <span className="studio-service__index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="studio-service__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="studio-team studio-team--compact" id="studio-team" aria-labelledby="studio-team-title">
          <div className="studio-team__intro" data-studio-reveal="up">
            <div>
              <p className="studio-kicker">核心团队</p>
              <h2 id="studio-team-title">
                灵感毫无保留，
                <span>商业精准赋能。</span>
              </h2>
            </div>
            <p>
              我们融合 AI 影像、先锋动画与时尚摄影，从美学判断到商业交付，让每一次品牌发声，都具有不可忽视的影响力。
            </p>
          </div>

          <div className="studio-team__groups">
            {teamGroups.map((group, groupIndex) => (
              <section
                className={`studio-team-group studio-team-group--${group.id}`}
                aria-labelledby={`studio-team-group-${group.id}`}
                key={group.id}
              >
                <header className="studio-team-group__head" data-studio-reveal="left">
                  <span aria-hidden="true">{String(groupIndex + 1).padStart(2, '0')}</span>
                  <h3 id={`studio-team-group-${group.id}`}>{group.title}</h3>
                </header>

                <div className="studio-team__members">
                  {group.members.map((member, memberIndex) => {
                    return (
                      <article
                        className={`studio-team-member studio-team-member--${member.id}`}
                        key={member.id}
                      >
                        <div
                          className="studio-team-member__reveal"
                          data-studio-reveal="scale"
                          style={revealDelay(memberIndex * 70)}
                        >
                          <div
                            className="studio-team-member__avatar"
                          >
                            <img
                              src={member.avatar}
                              alt={`${member.englishName} ${member.chineseName}`}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>

                          <div className="studio-team-member__identity">
                            <h4>
                              {member.englishName}
                              <small>{member.chineseName}</small>
                            </h4>
                          </div>

                          <div className="studio-team-member__honors">
                            <p>{member.credentials.join(' · ')}</p>
                          </div>

                          <div className="studio-team-member__details">
                            <div className="studio-team-member__details-clip">
                              <div className="studio-team-member__details-panel">
                                <strong>{member.focus}</strong>
                                <p>{member.summary}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </section>

        <section className="studio-brands" aria-labelledby="studio-brands-title">
          <div className="studio-brands__intro" data-studio-reveal="up">
            <div>
              <p className="studio-kicker">Selected Clients</p>
              <h2 id="studio-brands-title">与好品牌，共创下一帧。</h2>
            </div>
            <p>创作与交付经验覆盖科技、互联网、时尚、消费与城市建设等多元行业。</p>
          </div>
          <ul className="studio-brands__wall" aria-label="合作品牌">
            {brandLogos.map((brand, index) => (
              <li
                className={`studio-brand studio-brand--${brand.id}`}
                key={brand.name}
                data-studio-reveal="scale"
                style={revealDelay((index % 5) * 55)}
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  loading="lazy"
                  decoding="async"
                />
              </li>
            ))}
          </ul>
        </section>

        <StudioContactSection />
      </main>

      {selectedWork && (
        <WorkDetailModal
          work={selectedWork}
          isOpen={!!selectedWork}
          onClose={() => setSelectedWork(null)}
          isOwnProfile={false}
          displayMode="showcase"
        />
      )}
    </div>
  );
};

export default Studio;
